import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import useProgressBar from 'features/ad/progressBar/useProgressBar'
import {
  checkGenerable,
  createCampaign,
  fetchCampaign,
  fetchTrackInfo,
  reset,
  updateCampaign,
} from 'features/ad/campaign/campaignSlice'
import { useConfirm } from 'features/dialog/Confirm'
import { useHistory, useParams } from 'react-router-dom'
import { isEmptyObject } from 'utils'
import classNames from 'classnames'
import { fail, failWithErrorResponse, success } from 'features/toast/Toast'
import CampaignAdvancedOptionsSummary from 'features/ad/campaign/CampaignAdvancedOptionsSummary'
import Button from 'components/form/Button'
import LoadingWrapper from 'components/loading/LoadingWrapper'
import useDashboardReferrer from 'features/ad/useDashboardReferrer'
import useCampaignValidator from './hooks/useCampaignValidator'
import CampaignType from './form/CampaignType'
import CampaignName from './form/CampaignName'
import CampaignBizChannel from './form/CampaignBizChannel'
import ConversionTracking from './form/ConversionTracking'
import TraceUrl from './form/TraceUrl'
import CampaignDailyBudget from './form/CampaignDailyBudget'

const progress = [
  {
    text: '캠페인 만들기',
    active: true,
  },
  {
    text: '광고그룹 만들기',
  },
  {
    text: '키워드 만들기',
  },
  {
    text: '소재 선택하기',
  },
]

const Campaign = ({ modify = false }) => {
  const referrer = useDashboardReferrer()
  useProgressBar(modify ? null : progress)
  const dispatch = useDispatch()
  const history = useHistory()
  const { accountId: adAccountId, campaignId } = useParams()
  const { isLoading } = useSelector(state => state.ad.campaign)
  const { errors, validate, addError, clearError } = useCampaignValidator()
  const [isAdvancedOptionsOpen, setAdvancedOptionsOpen] = useState(false)
  const [PrevConfirm, showPrevConfirm] = useConfirm()
  const goBack = () => {
    history.push(`/${adAccountId}/dashboard`)
  }
  const handleSubmit = async () => {
    try {
      await validate()
      if (isEmptyObject(errors)) {
        const fetcher = modify ? updateCampaign() : createCampaign()
        const { error, payload } = await dispatch(fetcher)
        if (error) {
          const { errorCode } = payload
          if (errorCode === 103001) {
            fail(
              '등록 가능한 캠페인 개수를 초과하였습니다. \n미사용 캠페인을 삭제하세요.'
            )
          } else if (errorCode === 103002) {
            addError({
              name: '이미 사용 중인 캠페인 이름입니다. 캠페인이름을 변경하세요.',
            })
          } else if (errorCode === 103007) {
            addError({
              conversionTrackingId:
                '사용 불가한 픽셀&SDK 입니다. 다시 선택하세요.',
            })
          } else {
            failWithErrorResponse(payload, {
              defaultMessage:
                '알 수 없는 이유로 캠페인을 저장할 수 없습니다.\n다시 시도하세요.',
            })
          }
        } else {
          success(`캠페인이 ${modify ? '수정' : '등록'}되었습니다.`)
          if (modify) {
            goBack()
          } else {
            history.push(
              `/${adAccountId}/ads/campaigns/${payload.result.id}/groups/new${
                referrer ? `?from=${referrer}` : ''
              }`
            )
          }
        }
      }
    } catch (ignore) {}
  }
  useEffect(
    () => () => {
      clearError()
      dispatch(reset())
    },
    [dispatch, modify, clearError]
  )
  useEffect(() => {
    if (modify) {
      ;(async () => {
        const { error, payload } = await dispatch(fetchCampaign(campaignId))
        if (error) {
          const { errorCode, errorMessage } = payload
          if (errorCode === 409) {
            fail('삭제된 캠페인 입니다.')
          } else {
            fail(errorMessage || '알 수 없는 에러가 발생했습니다')
          }
          history.push(`/${adAccountId}/dashboard`)
        } else {
          if (payload?.campaign?.conversionTrackingId) {
            dispatch(fetchTrackInfo(payload.campaign.conversionTrackingId))
          }
        }
      })()
    }
  }, [adAccountId, campaignId, dispatch, history, modify])
  useEffect(() => {
    if (!modify) {
      dispatch(checkGenerable())
    }
  }, [dispatch, modify])

  return (
    <>
      <LoadingWrapper isLoading={isLoading}>
        <div className="wrap_adcreate">
          <div className="ad_managebox">
            <div className="managebox_tit">
              <strong className="tit_box">키워드광고 캠페인</strong>
            </div>
            <CampaignType modify={modify} />
            <CampaignBizChannel modify={modify} error={errors.bizChannelId} />
            <ConversionTracking
              modify={modify}
              error={errors.conversionTrackingId}
            />
            <CampaignName error={errors.name} />
            <div
              className={classNames('box_option', {
                on: isAdvancedOptionsOpen,
              })}>
              <div
                className={classNames('managebox_tit', {
                  error: errors.dailyBudgetAmount,
                })}
                onClick={() => setAdvancedOptionsOpen(!isAdvancedOptionsOpen)}>
                <strong className="tit_box">
                  고급
                  <span className="ico_fold" />
                </strong>
                {errors.dailyBudgetAmount ? (
                  <p className="txt_error">
                    잘못된 설정 내역이 있습니다. 다시 설정해 주세요.
                  </p>
                ) : modify ? (
                  <CampaignAdvancedOptionsSummary />
                ) : (
                  <p className="desc_box">
                    고급 옵션에서 추적URL과 일예산을 설정해보세요.
                  </p>
                )}
              </div>
              <TraceUrl />
              <CampaignDailyBudget error={errors.dailyBudgetAmount} />
            </div>
          </div>
          <div className="page_btn">
            <div className="inner_btn">
              <Button className="btn_gb" onClick={() => showPrevConfirm()}>
                취소
              </Button>
              <Button
                className="btn_gb gb_bl"
                onClick={handleSubmit}
                loading={isLoading}>
                {modify ? '저장' : '저장하고 계속'}
              </Button>
            </div>
          </div>
        </div>
      </LoadingWrapper>
      <PrevConfirm
        title={modify ? '캠페인 수정 취소' : '캠페인 만들기 취소'}
        onClose={({ isConfirmed }) => {
          if (isConfirmed) {
            goBack()
          }
        }}>
        캠페인 {modify ? '수정을' : '만들기를'} 취소하시겠습니까?
        <br />
        저장되지 않은 모든 내용은 사라집니다.
      </PrevConfirm>
    </>
  )
}

Campaign.propTypes = {
  modify: PropTypes.bool,
}

export default Campaign
